import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";

import { queryKeys } from "~/v2/api/queryKeys";
import type { Product } from "~/v2/api/rest/account";
import { POSITIONS_POLLING_INTERVAL } from "~/v2/constants";
import type { Position } from "~/v3-poc/api/positions.schema";
import type { DataTableColumns } from "~/v3-poc/components/DataTable";
import {
  useWorkspace,
  useWorkspaceStore,
} from "~/v3-poc/features/workspace/shared/workspace/Workspace.context";
import type {
  GroupId,
  OrderTicketDraft,
} from "~/v3-poc/features/workspace/shared/workspace/workspace.types";
import {
  useClosePositions,
  useGetPositions,
} from "~/v3-poc/hooks/useGetPositions";
import { useBoundWatchlistSymbols } from "~/v3-poc/hooks/useWatchlists";
import { useInvalidate } from "~/v3-poc/kernel/react/Invalidation.context";
import type { ClosePositionDialogProps } from "./ClosePositionDialog.connector";
import type { ClosePositionConfirmOptions } from "./ClosePositionDialog.controller";
import type { LiquidateAllDialogProps } from "./LiquidateAllDialog.connector";
import type { PositionsToolbarPresentationProps } from "./PositionsToolbar.pres";
import {
  createPositionsWidgetColumns,
  getPositionsColumnPresetIds,
  getPositionsWidgetColumnOptions,
  type PositionsRowActions,
  type PositionsSelectionConfig,
  type PositionsWidgetColumnId,
  selectVisiblePositionsWidgetColumns,
} from "./PositionsWidget.columns";
import type {
  PositionsTicketSeed,
  PositionsWidgetFilters,
  PositionsWidgetRow,
} from "./PositionsWidget.types";
import {
  arrangePositionRows,
  createDefaultPositionsFilters,
  createPositionCloseSeed,
  getPositionsBookTotal,
  getPositionsFiltersValidationError,
  mapPositionToRow,
} from "./PositionsWidget.utils";
import type { RollPositionDialogProps } from "./RollPositionDialog.connector";
import { usePositionsColumnVisibility } from "./usePositionsColumnVisibility";
import {
  type PositionsTableDensity,
  usePositionsTableDisplay,
} from "./usePositionsTableDisplay";

const REQUIRED_VISIBLE_COLUMN_IDS = ["symbol", "actions"] as const;
const NEW_TICKET_SIZE = { w: 4, h: 14 } as const;

export interface UsePositionsWidgetControllerParams {
  id: string;
  accountId: string | undefined;
  product: Product | undefined;
}

export interface PositionsWidgetControllerResult {
  widget: {
    surface: "workspace";
    filters: PositionsWidgetFilters;
    rows: PositionsWidgetRow[];
    openCount: number;
    columns: DataTableColumns<PositionsWidgetRow>;
    selectedIds: readonly string[];
    validationError?: string;
    isAccountReady: boolean;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    errorMessage?: string;
    density: PositionsTableDensity;
    horizontalLines: boolean;
    verticalLines: boolean;
    detailRowId: string | null;
    onRetry: () => void;
  };
  toolbar: PositionsToolbarPresentationProps;
  closeDialog: ClosePositionDialogProps;
  rollDialog: RollPositionDialogProps;
  liquidateDialog: LiquidateAllDialogProps;
  embeddedTicket: {
    symbol: string;
    quantity: number;
    onClose: () => void;
  } | null;
  detail: {
    rowId: string | null;
    dayHigh?: number;
    dayLow?: number;
    isWatched: boolean;
  };
}

const toOrderTicketDraft = (
  seed: PositionsTicketSeed,
): OrderTicketDraft | undefined =>
  seed.side && seed.quantity !== undefined && seed.quantity > 0
    ? {
        side: seed.side,
        quantity: seed.quantity,
        ...(seed.positionIntent ? { positionIntent: seed.positionIntent } : {}),
      }
    : undefined;

const usePositionsRows = ({
  boundWatchlistSymbols,
  positions,
}: {
  boundWatchlistSymbols: ReadonlySet<string>;
  positions: Position[];
}) => {
  const [filters, setFilters] = useState<PositionsWidgetFilters>(
    createDefaultPositionsFilters,
  );
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);
  const [bulkCloseRatio, setBulkCloseRatio] = useState(100);

  const {
    visibleColumnIds,
    setColumnVisibility,
    selectAllColumns,
    resetColumnVisibility,
    setVisibleColumnIds,
  } = usePositionsColumnVisibility(undefined, true);
  const {
    density,
    horizontalLines,
    verticalLines,
    setDensity,
    setHorizontalLines,
    setVerticalLines,
  } = usePositionsTableDisplay();

  const rows = useMemo(() => {
    const now = Date.now();
    const bookTotal = getPositionsBookTotal(positions);
    return arrangePositionRows(
      positions.map((position) => mapPositionToRow(position, now, bookTotal)),
      filters,
      boundWatchlistSymbols,
    );
  }, [boundWatchlistSymbols, filters, positions]);

  const rowsById = useMemo(
    () => new Map(rows.map((row) => [row.id, row])),
    [rows],
  );

  const visibleSelectedIds = useMemo(
    () => selectedIds.filter((selectedId) => rowsById.has(selectedId)),
    [rowsById, selectedIds],
  );

  const closeModalPositions = useMemo(
    () =>
      visibleSelectedIds
        .map((selectedId) => rowsById.get(selectedId))
        .filter((row): row is PositionsWidgetRow => row !== undefined),
    [rowsById, visibleSelectedIds],
  );

  const onToggleRow = useCallback((positionId: string, selected: boolean) => {
    setSelectedIds((current) => {
      if (selected) {
        return current.includes(positionId)
          ? current
          : [...current, positionId];
      }
      return current.filter((id) => id !== positionId);
    });
  }, []);

  const onSelectAllRows = useCallback(() => {
    setSelectedIds(rows.map((row) => row.id));
  }, [rows]);

  const onClearSelected = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const onFiltersChange = useCallback(
    (next: PositionsWidgetFilters) => {
      if (next.columnPreset !== filters.columnPreset) {
        setVisibleColumnIds(getPositionsColumnPresetIds(next.columnPreset));
      }
      setFilters(next);
    },
    [filters.columnPreset, setVisibleColumnIds],
  );

  const columnOptions = useMemo(
    () => getPositionsWidgetColumnOptions(true),
    [],
  );

  return {
    filters,
    selectedIds,
    setSelectedIds,
    bulkCloseRatio,
    setBulkCloseRatio,
    visibleColumnIds,
    setColumnVisibility,
    selectAllColumns,
    resetColumnVisibility,
    density,
    horizontalLines,
    verticalLines,
    setDensity,
    setHorizontalLines,
    setVerticalLines,
    rows,
    rowsById,
    visibleSelectedIds,
    closeModalPositions,
    onToggleRow,
    onSelectAllRows,
    onClearSelected,
    onFiltersChange,
    columnOptions,
  };
};

const usePositionsTicketRouting = ({
  groupId,
  store,
}: {
  groupId: GroupId | null;
  store: ReturnType<typeof useWorkspaceStore>;
}) => {
  const [embeddedTicket, setEmbeddedTicket] = useState<{
    symbol: string;
    quantity: number;
  } | null>(null);
  const [rollingRow, setRollingRow] = useState<PositionsWidgetRow | null>(null);

  const createTicket = useCallback((): string => {
    const state = store.getState();
    const ticketId = crypto.randomUUID();
    const nextY = Math.max(
      0,
      ...Object.values(state.placements).map(
        (placement) => placement.y + placement.h,
      ),
    );
    state.addWidget({
      id: ticketId,
      kind: "orderTicket",
      placement: { widgetId: ticketId, x: 0, y: nextY, ...NEW_TICKET_SIZE },
    });
    if (groupId !== null) {
      state.assignWidgetGroup(ticketId, groupId);
    }
    return ticketId;
  }, [groupId, store]);

  const resolveTicketId = useCallback((): string => {
    const state = store.getState();
    const tickets = Object.entries(state.widgets).filter(
      ([, widget]) => widget.kind === "orderTicket",
    );
    const sameGroup =
      groupId === null
        ? undefined
        : tickets.find(([, widget]) => widget.groupId === groupId);
    const target = sameGroup ?? tickets[0];
    return target ? target[0] : createTicket();
  }, [createTicket, groupId, store]);

  const seedTicket = useCallback(
    (seed: PositionsTicketSeed, options?: { preferNewTicket?: boolean }) => {
      const ticketId = options?.preferNewTicket
        ? createTicket()
        : resolveTicketId();
      const state = store.getState();
      state.setTicketSymbol(ticketId, seed.symbol);
      state.setTicketDraft(ticketId, toOrderTicketDraft(seed));
    },
    [createTicket, resolveTicketId, store],
  );

  const openTradeTicket = useCallback((row: PositionsWidgetRow) => {
    const quantity =
      row.quantityValue && row.quantityValue > 0 ? row.quantityValue : 1;
    setEmbeddedTicket({ symbol: row.symbol, quantity });
  }, []);

  const onCloseEmbeddedTicket = useCallback(() => {
    setEmbeddedTicket(null);
  }, []);

  const onRollRow = useCallback((row: PositionsWidgetRow) => {
    setRollingRow(row);
  }, []);

  const onConfirmRoll = useCallback(() => {
    if (rollingRow) {
      seedTicket(createPositionCloseSeed(rollingRow));
    }
    setRollingRow(null);
  }, [rollingRow, seedTicket]);

  const onRollDialogOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setRollingRow(null);
    }
  }, []);

  return {
    embeddedTicket,
    rollingRow,
    openTradeTicket,
    onCloseEmbeddedTicket,
    onRollRow,
    onConfirmRoll,
    onRollDialogOpenChange,
    seedTicket,
  };
};

const usePositionsDestructiveFlows = ({
  currentAccountId,
  product,
  invalidate,
  positions,
  rowsById,
  visibleSelectedIds,
  setSelectedIds,
}: {
  currentAccountId: string | undefined;
  product: Product | undefined;
  invalidate: ReturnType<typeof useInvalidate>;
  positions: Position[];
  rowsById: Map<string, PositionsWidgetRow>;
  visibleSelectedIds: readonly string[];
  setSelectedIds: Dispatch<SetStateAction<readonly string[]>>;
}) => {
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closeModalRatioPct, setCloseModalRatioPct] = useState<
    number | undefined
  >();
  const [liquidateOpen, setLiquidateOpen] = useState(false);
  const [liquidateCancelOrders, setLiquidateCancelOrders] = useState(false);

  const liquidatePositions = useClosePositions({
    meta: { showErrorToast: false },
    onError: () => undefined,
    onSuccess: () => {
      invalidate({ queryKey: queryKeys.positions.all });
      invalidate({ queryKey: queryKeys.orders.all });
      setLiquidateOpen(false);
      setLiquidateCancelOrders(false);
      setCloseModalOpen(false);
      setSelectedIds([]);
    },
  });

  const onRequestCloseInRatio = useCallback((ratioPct = 100) => {
    setCloseModalRatioPct(ratioPct);
    setCloseModalOpen(true);
  }, []);

  const onCloseRow = useCallback(
    (row: PositionsWidgetRow) => {
      setSelectedIds([row.id]);
      setCloseModalRatioPct(undefined);
      setCloseModalOpen(true);
    },
    [setSelectedIds],
  );

  const onLiquidateOpenChange = useCallback((open: boolean) => {
    setLiquidateOpen(open);
    if (!open) {
      setLiquidateCancelOrders(false);
    }
  }, []);

  const onConfirmLiquidate = useCallback(() => {
    if (!currentAccountId || !product) {
      return;
    }
    liquidatePositions.mutate({
      accountID: currentAccountId,
      product: product === "live" ? "live" : "paper",
      symbols: positions.map((position) => position.symbol),
      cancelOrders: liquidateCancelOrders,
    });
  }, [
    currentAccountId,
    liquidateCancelOrders,
    liquidatePositions,
    positions,
    product,
  ]);

  const onConfirmClose = useCallback(
    ({
      cancelOrders,
      closeRatioPct,
      partialQty,
    }: ClosePositionConfirmOptions) => {
      if (!currentAccountId || !product) {
        return;
      }
      const selectedRows = visibleSelectedIds
        .map((selectedId) => rowsById.get(selectedId))
        .filter((row): row is PositionsWidgetRow => row !== undefined);
      if (selectedRows.length === 0) {
        return;
      }
      if (partialQty != null && partialQty <= 0) {
        return;
      }
      let percentage: string | undefined;
      if (closeRatioPct != null && closeRatioPct < 100) {
        percentage = String(closeRatioPct);
      } else if (
        partialQty != null &&
        selectedRows.length === 1 &&
        selectedRows[0].quantityValue
      ) {
        const max = selectedRows[0].quantityValue;
        const pct = Math.round((partialQty / max) * 100);
        if (pct <= 0) {
          return;
        }
        if (pct < 100) {
          percentage = String(pct);
        }
      }
      liquidatePositions.mutate({
        accountID: currentAccountId,
        product: product === "live" ? "live" : "paper",
        symbols: selectedRows.map((row) => row.symbol),
        ...(percentage ? { percentage } : {}),
        cancelOrders,
      });
    },
    [
      currentAccountId,
      liquidatePositions,
      product,
      rowsById,
      visibleSelectedIds,
    ],
  );

  return {
    closeModalOpen,
    closeModalRatioPct,
    liquidateOpen,
    liquidateCancelOrders,
    liquidatePositions,
    onRequestCloseInRatio,
    onCloseRow,
    onLiquidateOpenChange,
    onConfirmLiquidate,
    onConfirmClose,
    setLiquidateCancelOrders,
    setCloseModalOpen,
  };
};

const usePositionsColumns = ({
  visibleColumnIds,
  rows,
  visibleSelectedIds,
  openTradeTicket,
  onRollRow,
  onCloseRow,
  onToggleRow,
  onSelectAllRows,
  onClearSelected,
}: {
  visibleColumnIds: readonly PositionsWidgetColumnId[];
  rows: PositionsWidgetRow[];
  visibleSelectedIds: readonly string[];
  openTradeTicket: (row: PositionsWidgetRow) => void;
  onRollRow: (row: PositionsWidgetRow) => void;
  onCloseRow: (row: PositionsWidgetRow) => void;
  onToggleRow: (positionId: string, selected: boolean) => void;
  onSelectAllRows: () => void;
  onClearSelected: () => void;
}) =>
  useMemo(() => {
    const actions: PositionsRowActions = {
      onTrade: openTradeTicket,
      onRoll: onRollRow,
      onClose: onCloseRow,
    };
    const selectedIdSet = new Set(visibleSelectedIds);
    const selection: PositionsSelectionConfig = {
      selectedIds: selectedIdSet,
      onToggle: onToggleRow,
      onToggleAll: (selected) => {
        if (selected) {
          onSelectAllRows();
        } else {
          onClearSelected();
        }
      },
      allSelected: rows.length > 0 && visibleSelectedIds.length === rows.length,
      someSelected:
        visibleSelectedIds.length > 0 &&
        visibleSelectedIds.length < rows.length,
    };
    return selectVisiblePositionsWidgetColumns(
      visibleColumnIds,
      createPositionsWidgetColumns(actions, selection),
    );
  }, [
    onClearSelected,
    onCloseRow,
    onRollRow,
    onSelectAllRows,
    onToggleRow,
    openTradeTicket,
    rows,
    visibleColumnIds,
    visibleSelectedIds,
  ]);

export const usePositionsWidgetController = ({
  id,
  accountId,
  product,
}: UsePositionsWidgetControllerParams): PositionsWidgetControllerResult => {
  const invalidate = useInvalidate();
  const store = useWorkspaceStore();
  const groupId = useWorkspace((state) => state.widgets[id]?.groupId ?? null);
  const boundWatchlistSymbols = useBoundWatchlistSymbols();

  const isAccountReady = Boolean(accountId && product);

  const { positions, isLoading, isFetching, isError, error, refetch } =
    useGetPositions({
      accountID: accountId,
      product,
      options: {
        enabled: isAccountReady,
        refetchInterval: POSITIONS_POLLING_INTERVAL,
      },
    });

  const rowsState = usePositionsRows({
    boundWatchlistSymbols,
    positions,
  });
  const routing = usePositionsTicketRouting({ groupId, store });
  const destructive = usePositionsDestructiveFlows({
    currentAccountId: accountId,
    product,
    invalidate,
    positions,
    rowsById: rowsState.rowsById,
    visibleSelectedIds: rowsState.visibleSelectedIds,
    setSelectedIds: rowsState.setSelectedIds,
  });
  const columns = usePositionsColumns({
    visibleColumnIds: rowsState.visibleColumnIds,
    rows: rowsState.rows,
    visibleSelectedIds: rowsState.visibleSelectedIds,
    openTradeTicket: routing.openTradeTicket,
    onRollRow: routing.onRollRow,
    onCloseRow: destructive.onCloseRow,
    onToggleRow: rowsState.onToggleRow,
    onSelectAllRows: rowsState.onSelectAllRows,
    onClearSelected: rowsState.onClearSelected,
  });

  const hiddenByFilterCount = Math.max(
    0,
    positions.length - rowsState.rows.length,
  );

  const onRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const validationError = getPositionsFiltersValidationError(rowsState.filters);

  return {
    widget: {
      surface: "workspace",
      filters: rowsState.filters,
      rows: rowsState.rows,
      openCount: positions.length,
      columns,
      selectedIds: rowsState.visibleSelectedIds,
      validationError,
      isAccountReady,
      isLoading,
      isFetching,
      isError,
      errorMessage: error?.message,
      density: rowsState.density,
      horizontalLines: rowsState.horizontalLines,
      verticalLines: rowsState.verticalLines,
      detailRowId: null,
      onRetry,
    },
    toolbar: {
      surface: "workspace",
      filters: rowsState.filters,
      onFiltersChange: rowsState.onFiltersChange,
      columnOptions: rowsState.columnOptions,
      visibleColumnIds: rowsState.visibleColumnIds,
      requiredVisibleColumnIds: REQUIRED_VISIBLE_COLUMN_IDS,
      onColumnVisibilityChange: rowsState.setColumnVisibility,
      onSelectAllColumns: rowsState.selectAllColumns,
      onResetColumns: rowsState.resetColumnVisibility,
      density: rowsState.density,
      horizontalLines: rowsState.horizontalLines,
      verticalLines: rowsState.verticalLines,
      onDensityChange: rowsState.setDensity,
      onHorizontalLinesChange: rowsState.setHorizontalLines,
      onVerticalLinesChange: rowsState.setVerticalLines,
      openCount: positions.length,
      selectedCount: rowsState.visibleSelectedIds.length,
      bulkCloseRatio: rowsState.bulkCloseRatio,
      onBulkCloseRatioChange: rowsState.setBulkCloseRatio,
      onClearSelected: rowsState.onClearSelected,
      onRequestCloseInRatio: destructive.onRequestCloseInRatio,
      onLiquidateOpenChange: destructive.onLiquidateOpenChange,
      isLoading,
      isFetching,
    },
    closeDialog: {
      open: destructive.closeModalOpen,
      positions: rowsState.closeModalPositions,
      closeModalRatioPct: destructive.closeModalRatioPct,
      submitting: destructive.liquidatePositions.isPending,
      errorMessage: destructive.liquidatePositions.error?.message,
      onOpenChange: destructive.setCloseModalOpen,
      onConfirmClose: destructive.onConfirmClose,
    },
    rollDialog: {
      row: routing.rollingRow,
      onOpenChange: routing.onRollDialogOpenChange,
      onConfirmRoll: routing.onConfirmRoll,
    },
    liquidateDialog: {
      open: destructive.liquidateOpen,
      openCount: positions.length,
      hiddenByFilterCount,
      cancelOrders: destructive.liquidateCancelOrders,
      submitting: destructive.liquidatePositions.isPending,
      errorMessage: destructive.liquidatePositions.error?.message,
      onOpenChange: destructive.onLiquidateOpenChange,
      onCancelOrdersChange: destructive.setLiquidateCancelOrders,
      onConfirm: destructive.onConfirmLiquidate,
    },
    embeddedTicket: routing.embeddedTicket
      ? {
          symbol: routing.embeddedTicket.symbol,
          quantity: routing.embeddedTicket.quantity,
          onClose: routing.onCloseEmbeddedTicket,
        }
      : null,
    detail: {
      rowId: null,
      dayHigh: undefined,
      dayLow: undefined,
      isWatched: false,
    },
  };
};
